import axios from 'axios';
import { API_BASE_URL } from '../core/env.configs';
import { getJWTToken } from '../utils/helper';
import { RANGE_KIND, REPORT_SCOPE, REPORT_DELIVERY } from './companyReportApi';

/**
 * Data layer for Shoorah-staff PDF reports.
 *
 * The mirror of api/companyReportApi.js: same six calls, same shapes, against
 * `admin/v1/reports/*` instead of `company/v1/reports/*`. The two are
 * interchangeable at the call site, which is what lets the download button,
 * the dialog and the reports list take an `api` prop and serve both panels
 * without knowing which one they are in.
 *
 * WHAT DIFFERS is the body. A company report is implicitly about the company
 * in the token, so it only carries filters. A staff report has to say WHO it
 * is about — one company, one user, or the platform.
 */

const BASE_URL = API_BASE_URL || 'https://staging-api.shoorah.io/';

const ok = (res) => res?.data?.meta?.code === 1;
const payload = (res) => res?.data?.data;

const headers = () => ({ Authorization: getJWTToken(), deviceType: 3 });

// Re-exported so a consumer importing one api module gets the constants too,
// exactly as it would from companyReportApi.
export { RANGE_KIND, REPORT_SCOPE, REPORT_DELIVERY };

/**
 * The subject of a staff report.
 *
 * Only one of these is ever sent. A userId means one person, a companyId means
 * one company, and neither means the platform — narrowed by accountType if the
 * caller asked for just B2C or just B2B.
 */
export const reportSubject = ({ companyId = null, userId = null, accountType = '' } = {}) => {
  if (userId) return { userId };
  if (companyId) return { companyId };
  return accountType ? { accountType } : {};
};

/**
 * Turn the dialog's period choice plus a subject into the request body.
 *
 * Deliberately the same signature as companyReportApi's `buildReportBody` so
 * the dialog can call whichever it was handed. `filters` is accepted and
 * ignored: the staff reports have no demographic filter yet, and dropping the
 * parameter would mean the dialog had to know which api it holds.
 */
export const buildReportBody = ({ scope, featureKey, plane, period, delivery, subject }) => ({
  scope,
  ...(featureKey ? { featureKey } : {}),
  ...(plane ? { plane } : {}),
  ...reportSubject(subject),

  rangeKind: period?.kind || RANGE_KIND.DAYS,
  days: period?.days ?? 30,
  ...(period?.month ? { month: period.month } : {}),
  ...(period?.year ? { year: period.year } : {}),

  ...(delivery ? { delivery } : {})
});

/** Periods this admin may pick, and which features have a report. */
export const fetchReportOptions = async () => {
  try {
    const res = await axios.get(`${BASE_URL}admin/v1/reports/options`, { headers: headers() });
    return ok(res) ? payload(res) : null;
  } catch (error) {
    return null;
  }
};

/**
 * One feature, rendered inside the request and saved to disk.
 *
 * Returns an error string rather than throwing, because a failure here is a
 * message in the dialog, not a crash. A JSON body on a blob request is the
 * backend's validation error and has to be read out of the blob, since
 * responseType applies to failures too.
 */
export const downloadFeatureReport = async (body) => {
  try {
    const res = await axios.post(`${BASE_URL}admin/v1/reports/download`, body, {
      headers: headers(),
      responseType: 'blob'
    });

    if (res?.data?.type === 'application/json') {
      const text = await res.data.text();
      return { error: JSON.parse(text)?.meta?.message || 'Report could not be generated' };
    }

    const fileName =
      res.headers?.['content-disposition']?.split('filename=')?.[1]?.replace(/"/g, '') ||
      'shoorah-report.pdf';

    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);

    return { fileName };
  } catch (error) {
    let message = 'Report could not be generated';
    try {
      const body_ = error?.response?.data;
      if (body_ instanceof Blob) {
        message = JSON.parse(await body_.text())?.meta?.message || message;
      }
    } catch (parseError) {
      // keep the generic message
    }
    return { error: message };
  }
};

/** Queue a section or whole-dashboard report. Resolves once it is accepted. */
export const requestReport = async (body) => {
  try {
    const res = await axios.post(`${BASE_URL}admin/v1/reports/generate`, body, {
      headers: headers()
    });
    return ok(res)
      ? { job: payload(res) }
      : { error: res?.data?.meta?.message || 'Request failed' };
  } catch (error) {
    return { error: error?.response?.data?.meta?.message || 'Request failed' };
  }
};

/** This admin's reports, newest first. */
export const fetchReportJobs = async (page = 1, perPage = 10) => {
  try {
    const res = await axios.get(`${BASE_URL}admin/v1/reports/jobs`, {
      headers: headers(),
      params: { page, perPage }
    });
    if (!ok(res)) return { jobs: [], total: 0 };
    return { jobs: payload(res) || [], total: res?.data?.meta?.totalRecords || 0 };
  } catch (error) {
    return { jobs: [], total: 0 };
  }
};

/** Open a finished report. The link is signed and short-lived. */
export const openReportJob = async (jobId) => {
  try {
    const res = await axios.get(`${BASE_URL}admin/v1/reports/jobs/${jobId}/link`, {
      headers: headers()
    });
    if (!ok(res)) return { error: res?.data?.meta?.message || 'Report is not ready yet' };

    const { url, fileName } = payload(res) || {};
    if (!url) return { error: 'Report is not ready yet' };

    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'shoorah-report.pdf';
    link.target = '_blank';
    link.rel = 'noopener';
    link.click();

    return { fileName };
  } catch (error) {
    return { error: 'Report is not ready yet' };
  }
};

export const deleteReportJob = async (jobId) => {
  try {
    const res = await axios.delete(`${BASE_URL}admin/v1/reports/jobs/${jobId}`, {
      headers: headers()
    });
    return ok(res);
  } catch (error) {
    return false;
  }
};

/** One module the report components can be handed wholesale. */
export const adminReportApi = {
  buildReportBody,
  fetchReportOptions,
  downloadFeatureReport,
  requestReport,
  fetchReportJobs,
  openReportJob,
  deleteReportJob
};

export default adminReportApi;
